import type { DragEndEvent, DragOverEvent } from '@dnd-kit/react';
import type { dashboardSection } from '../sections/DefaultSections';

import { move } from '@dnd-kit/helpers';

import { defaultSections } from '../sections/DefaultSections';

export type customizableDashboardData = {
  sections: dashboardSection;
  sectionsOrder: string[];
};

export type customizableDashboardState = customizableDashboardData & {
  originalState: customizableDashboardData;
  dirty: boolean;
};

type customizableDashboardStateAction =
  | {
      type: 'reset';
      data: customizableDashboardData;
      registeredSections: dashboardSection;
    }
  | { type: 'hide'; key: string }
  | { type: 'show'; key: string }
  | { type: 'remove'; key: string }
  | { type: 'swap'; event: DragEndEvent }
  | { type: 'move-between-groups'; event: DragOverEvent }
  | { type: 'rollback' }
  | { type: 'commit' };

export const initializeCustomizableDashboardState = (
  state: customizableDashboardData,
  registeredSections: dashboardSection,
): customizableDashboardState => {
  const newSectionIds = Object.keys(registeredSections).filter(
    (sectionId) => !state.sectionsOrder.includes(sectionId),
  );

  const mergedState: customizableDashboardData = {
    sections: Object.fromEntries(
      Object.entries({ ...registeredSections, ...state.sections }).map(
        ([id, section]) => [
          id,
          {
            ...section,
            label: Object.hasOwn(defaultSections.sections, id)
              ? defaultSections.sections[id].label
              : section.label,
          },
        ],
      ),
    ),
    sectionsOrder: [...state.sectionsOrder, ...newSectionIds],
  };

  return {
    ...mergedState,
    originalState: mergedState,
    dirty: false,
  };
};

export const removeDashboardSection = (
  data: customizableDashboardData,
  sectionId: string,
): customizableDashboardData => ({
  sections: Object.fromEntries(
    Object.entries(data.sections).filter(([id]) => id !== sectionId),
  ),
  sectionsOrder: data.sectionsOrder.filter((id) => id !== sectionId),
});

export const customizableDashboardReducer = (
  state: customizableDashboardState,
  action: customizableDashboardStateAction,
): customizableDashboardState => {
  const newState = { ...state };

  switch (action.type) {
    case 'reset':
      return initializeCustomizableDashboardState(
        action.data,
        action.registeredSections,
      );
    case 'hide':
      return {
        ...state,
        sections: {
          ...state.sections,
          [action.key]: {
            ...state.sections[action.key],
            visible: false,
          },
        },
        dirty: true,
      };

    case 'show':
      return {
        ...state,
        sections: {
          ...state.sections,
          [action.key]: {
            ...state.sections[action.key],
            visible: true,
          },
        },
        dirty: true,
      };
    case 'remove':
      return {
        ...state,
        ...removeDashboardSection(state, action.key),
        originalState: removeDashboardSection(state.originalState, action.key),
      };
    case 'swap': {
      newState.sectionsOrder = move(newState.sectionsOrder, action.event);
      newState.dirty = true;
      return newState;
    }
    case 'move-between-groups': {
      const groups = {
        active: state.sectionsOrder.filter(
          (sectionId) => state.sections[sectionId].visible,
        ),
        available: state.sectionsOrder.filter(
          (sectionId) => !state.sections[sectionId].visible,
        ),
      };
      const movedGroups = move(groups, action.event);
      const activeSectionIds = new Set(movedGroups.active);

      return {
        ...state,
        sectionsOrder: [...movedGroups.active, ...movedGroups.available],
        dirty: true,
        sections: Object.fromEntries(
          Object.entries(state.sections).map(([sectionId, section]) => [
            sectionId,
            {
              ...section,
              visible: activeSectionIds.has(sectionId),
            },
          ]),
        ),
      };
    }
    case 'rollback':
      return {
        ...state.originalState,
        originalState: state.originalState,
        dirty: false,
      };
    case 'commit': {
      const committedState = {
        sections: state.sections,
        sectionsOrder: state.sectionsOrder,
      };

      return {
        ...committedState,
        originalState: committedState,
        dirty: false,
      };
    }
    default:
      return newState;
  }
};
